import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Image, Platform, RefreshControl, BackHandler, ActivityIndicator } from 'react-native'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { useFocusEffect, useNavigation } from '@react-navigation/native'
import { EmptyState, Alert } from '../../components/Pick/ui'
import SegmentedTabs from '../../components/SegmentedTabs'
import { ListSkeleton, ErrorState } from '../../components/StateViews'
import { shouldWarn, confirmLeave } from '../../lib/pickGuard'
import RaceSwitcher from '../../components/Pick/RaceSwitcher'
import StageSwitcher from '../../components/Pick/StageSwitcher'
import StageProfile from '../../components/Pick/StageProfile'
import { PickScrollContext } from '../../components/Pick/scrollContext'
import TabellaLive from './TabellaLive'
import TabellaLivePietre from './TabellaLivePietre'
import InserimentoPick from './InserimentoPick'
import RiepilogoPick from './RiepilogoPick'
import ModaleStartlist from './ModaleStartlist'
import { colors, fonts, radius } from '../../lib/theme'
import ErrorBoundary from '../../components/ErrorBoundary'
import { useThemeColors } from '../../lib/useThemeColors'
import * as Sharing from 'expo-sharing'
import { Linking } from 'react-native'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { LOGO } from '../../lib/assets'
import { JerseyAsOf } from '../../lib/useSpecialJerseys'

// Su nativo lo scroll della pagina e' un NestableScrollContainer: la lista dei corridori trascinabile
// (InserimentoPick) ci sta dentro senza due scroll/gesture in competizione. Su web resta un ScrollView.
const PageScroll = (() => {
  if (Platform.OS === 'web') return ScrollView
  try { return require('react-native-draggable-flatlist').NestableScrollContainer ?? ScrollView } catch { return ScrollView }
})()

export default function Pick() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { user, profile, nickname, leagueId, leagueName, userLeagues, isAdmin } = useAuth()
  const params = useLocalSearchParams()

  const [allData, setAllData] = useState([])
  const [stageCats, setStageCats] = useState({})   // id_stage -> id_stage_cat (1 pianura, 2 montagna, 3 collina)
  const [selGara, setSelGara] = useState(null)
  const [selTappa, setSelTappa] = useState(null)
  const [activeTab, setActiveTab] = useState('inserimento')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tourBloccati, setTourBloccati] = useState([])
  const [sharing, setSharing] = useState(false)
  const [showShareOptions, setShowShareOptions] = useState(false)
  const [shareMode, setShareMode] = useState('top25')
  const [liveData, setLiveData] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [showStartlist, setShowStartlist] = useState(false)

  const raceColorsRef = useRef({})   // id_race -> { gc, pt, gm, gmPattern }, per non perderli quando si cambia gara a mano

  // Scroll condiviso con i componenti annidati (intestazione fissa della matrice nella vista live)
  const rootRef = useRef(null)
  const viewportTopRef = useRef(0)
  const scrollListeners = useRef(new Set())
  const [stickyNode, setStickyNode] = useState(null)
  // NestableScrollContainer sostituisce onScroll con un suo handler (la prop passata viene ignorata su nativo):
  // invece di ascoltare lo scroll, chi e' iscritto viene richiamato a intervalli brevi, solo finche' ce ne sono.
  const pollTimer = useRef(null)
  const scrollCtx = useMemo(() => ({
    subscribe: (fn) => {
      scrollListeners.current.add(fn)
      if (!pollTimer.current) pollTimer.current = setInterval(() => scrollListeners.current.forEach(f => f()), 100)
      return () => {
        scrollListeners.current.delete(fn)
        if (scrollListeners.current.size === 0 && pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null }
      }
    },
    getViewportTop: () => viewportTopRef.current,
    setSticky: setStickyNode,
  }), [])
  const measureViewport = () => { rootRef.current?.measureInWindow?.((x, y) => { viewportTopRef.current = y }) }
  const manualRaceRef = useRef(null)
  const manualStageRef = useRef(null)
  const shareData = useRef(null)
  const viewShotRef = useRef(null)

  const currentLeague = userLeagues?.find(l => l.id_league === leagueId)
  const hasFlip = currentLeague?.flip ?? false

  // Bug corretto: le dipendenze erano vuote ([]), quindi preRaceId/preStageId
  // venivano calcolati una sola volta al primo montaggio della schermata e mai
  // più aggiornati — se il tab Pick resta montato in memoria (comune con Expo
  // Router), premere "VAI" da Home su una gara diversa non aveva alcun effetto:
  // la schermata continuava a usare il primissimo id_race/id_stage mai ricevuto.
  const preRaceId = useMemo(() => params.id_race ? parseInt(params.id_race) : null, [params.id_race])
  const preStageId = useMemo(() => params.id_stage ? parseInt(params.id_stage) : null, [params.id_stage])

  // Android: il tasto indietro con una selezione non salvata chiede conferma (solo mentre questa pagina e' in primo piano)
  const navigation = useNavigation()
  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== 'android') return undefined
      const onBack = () => {
        if (!shouldWarn()) return false              // comportamento normale del sistema
        confirmLeave(t, () => navigation.goBack())
        return true
      }
      const sub = BackHandler.addEventListener('hardwareBackPress', onBack)
      return () => sub.remove()
    }, [t, navigation])
  )

  const handleDataReady = useCallback((data) => {
    if (data?.rows !== undefined) { shareData.current = { ...shareData.current, rows: data.rows }; setLiveData(data.rows) }
    if (data?.jerseys !== undefined) shareData.current = { ...shareData.current, jerseys: data.jerseys }
    if (data?.colors !== undefined) shareData.current = { ...shareData.current, colors: data.colors }
  }, [])

  const handleShare = async () => {
    if (!showShareOptions) { setShowShareOptions(true); return }
    if (!viewShotRef.current) return
    setSharing(true)
    try {
      const { captureRef } = await import('react-native-view-shot')
      const uri = await captureRef(viewShotRef, { format: 'png', quality: 1 })
      if (Platform.OS === 'web') {
        const a = document.createElement('a')
        a.href = uri
        a.download = `virtua-picks-${selGara?.name?.replace(/\s+/g, '-')}-t${selTappa?.stage ?? 1}.png`
        a.click()
      } else {
        await Sharing.shareAsync(uri, {
          mimeType: 'image/png',
          dialogTitle: `${selGara?.name} — ${t('common.stage')} ${selTappa?.stage ?? ''}`,
          UTI: 'public.png',
        })
      }
    } catch (e) {
      console.error('Share error:', e)
    } finally {
      setSharing(false)
      setShowShareOptions(false)
    }
  }

  const loadData = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('fn_stage_all_status', { p_id_league: leagueId })
      if (error) throw error

      const combined = (data ?? []).sort((a, b) => new Date(`${a.stage_date} ${a.stage_time}`) - new Date(`${b.stage_date} ${b.stage_time}`))
      setAllData(combined)
      if (combined.length === 0) { setLoading(false); return }

      // profilo altimetrico delle tappe dei giri (le gare di un giorno non ne hanno)
      const tourRaceIds = [...new Set(combined.filter(d => d.id_type_race !== 3).map(d => d.id_race))]
      if (tourRaceIds.length) {
        const { data: cats } = await supabase.from('dim_race_stage').select('id_stage, id_stage_cat').in('id_race', tourRaceIds)
        setStageCats(Object.fromEntries((cats ?? []).map(c => [c.id_stage, c.id_stage_cat])))
      }

      if (user?.id) {
        const { data: bloccati } = await supabase.rpc('fn_tour_bloccati', { p_id_league: leagueId, p_id_user: user.id })
        setTourBloccati(bloccati ?? [])
      }

      let startRaceId = preRaceId ?? manualRaceRef.current
      let startStageId = preStageId ?? manualStageRef.current

      if (!startRaceId) {
        const liveStage = combined.find(d => d.stage_status === 'live')
        if (liveStage) {
          startRaceId = liveStage.id_race
          startStageId = liveStage.id_stage
        } else {
          const { data: next } = await supabase.rpc('fn_next_pickable_stage', { p_id_league: leagueId })
          if (next?.[0]) {
            startRaceId = next[0].id_race
            startStageId = next[0].id_stage
          } else {
            const lastFinished = [...combined].reverse().find(d => d.stage_status === 'finished')
            if (lastFinished) { startRaceId = lastFinished.id_race; startStageId = lastFinished.id_stage }
          }
        }
      }
      const gareMap = new Map()
      combined.forEach(d => { if (!gareMap.has(d.id_race)) gareMap.set(d.id_race, { id: d.id_race, name: d.race_name, type: d.id_type_race, colors: {} }) })

      const { data: raceColors } = await supabase.from('dim_race').select('id_race, color_gc, color_pt, color_gm').in('id_race', [...gareMap.keys()])
        ; (raceColors ?? []).forEach(r => { if (gareMap.has(r.id_race)) gareMap.get(r.id_race).colors = { gc: r.color_gc, pt: r.color_pt, gm: r.color_gm } })
      // stile della maglia GPM (pois): query a parte e tollerante, se la colonna non c'e' ancora resta a tinta unita
      const { data: pats } = await supabase.from('dim_race').select('id_race, gm_pattern').in('id_race', [...gareMap.keys()])
      ;(pats ?? []).forEach(r => { const g = gareMap.get(r.id_race); if (g) g.colors = { ...g.colors, gmPattern: r.gm_pattern ?? null } })
      raceColorsRef.current = Object.fromEntries([...gareMap.values()].map(g => [g.id, g.colors]))

      const gare = Array.from(gareMap.values())
      const initGara = gare.find(g => g.id === startRaceId) ?? gare[gare.length - 1]
      setSelGara(initGara)
      const tappe = combined.filter(d => d.id_race === initGara.id)
      setSelTappa(tappe.find(tappa => tappa.id_stage === startStageId) ?? tappe[0])
    } catch { setError(t('pick.error_load')) }
    finally { setLoading(false) }
  }, [preRaceId, preStageId, leagueId, t, user?.id])

  const onRefresh = useCallback(async () => {
    setRefreshing(true)
    await loadData()
    setRefreshing(false)
  }, [loadData])

  useEffect(() => { loadData() }, [loadData])

  // ── Gare: passate / attive (già iniziate) / future (non ancora iniziate) ──
  // "passata" = tutte le tappe concluse (stage_status). "iniziata" invece la
  // calcoliamo sulla DATA reale della prima tappa, non su stage_status —
  // quel campo è aggiornato manualmente dall'admin e può essere in ritardo;
  // una gara il cui giorno di partenza è oggi o nel passato è "in corso"
  // nella realtà anche se stage_status non è ancora stato aggiornato a mano.
  const allRaces = Array.from(new Map(allData.map(d => [d.id_race, { value: d.id_race, label: d.race_name, type: d.id_type_race }])).values())
  const raceIsFullyPast = (idRace) => allData.filter(d => d.id_race === idRace).every(d => d.stage_status === 'finished')

  // Selettore gara (stile VB): in linea poche gare attive, le altre nello sheet storico.
  const MAX_RACE_PILLS = 3
  const raceInfo = allRaces.map(r => {
    const rows = allData.filter(d => d.id_race === r.value)
    const status = rows.some(d => d.stage_status === 'live') ? 'live' : raceIsFullyPast(r.value) ? 'done' : 'pick'
    return { ...r, status, date: rows[0]?.stage_date }
  })
  const activeRaces = raceInfo.filter(r => r.status !== 'done')
  const pastRaces = raceInfo.filter(r => r.status === 'done').reverse() // dalla piu' recente
  const selRace = raceInfo.find(r => r.value === selGara?.id)
  let racePills = activeRaces.slice(0, MAX_RACE_PILLS)
  // la gara selezionata si vede sempre: se e' lontana o passata prende l'ultimo posto in linea
  if (selRace && !racePills.some(r => r.value === selRace.value)) racePills = [...racePills.slice(0, MAX_RACE_PILLS - 1), selRace]
  if (!racePills.length && pastRaces.length) racePills = [pastRaces[0]]
  const shownIds = new Set(racePills.map(r => r.value))
  const racesNextMore = activeRaces.filter(r => !shownIds.has(r.value))
  const racesPastMore = pastRaces.filter(r => !shownIds.has(r.value))

  const tappeGara = allData.filter(d => selGara && d.id_race === selGara.id)

  // Tappe per il selettore (le gare in linea, tipo 3, hanno una sola tappa: nessun selettore)
  const stageItems = selGara?.type === 3 ? [] : tappeGara.map(tappa => ({
    value: tappa.id_stage,
    number: tappa.stage,
    date: tappa.stage_date,
    status: tappa.stage_status === 'live' ? 'live' : tappa.stage_status === 'finished' ? 'done' : 'pick',
    profile: tappa.id_stage_type === 4 ? 'itt' : tappa.id_stage_type === 5 ? 'ttt' : ({ 1: 'flat', 2: 'mountain', 3: 'hilly' })[stageCats[tappa.id_stage]] ?? null,
    data: tappa,
  }))

  const onGaraSelect = (item) => {
    const tappe = allData.filter(d => d.id_race === item.value)
    setSelGara({ id: item.value, name: item.label, type: item.type, colors: raceColorsRef.current[item.value] ?? {} })
    setSelTappa(tappe[0] ?? null)
    setActiveTab('inserimento')
    manualRaceRef.current = item.value
    manualStageRef.current = tappe[0]?.id_stage ?? null
  }

  const onTappaSelect = (item) => {
    if (selGara?.type === 3) return
    setSelTappa(item.data)
    setActiveTab('inserimento')
    manualStageRef.current = item.data?.id_stage ?? null
  }

  const showAllPicks = selTappa?.stage_status === 'live' || selTappa?.stage_status === 'finished'
  const isTour = selGara?.type !== 3
  const garaBloccata = tourBloccati.find(b => b.id_race === selGara?.id) ?? null

  if (loading) return <View style={styles.loadingWrap}><ListSkeleton rows={5} header={false} /></View>

  return (
    <PickScrollContext.Provider value={scrollCtx}>
    <View ref={rootRef} style={{ flex: 1, backgroundColor: tc.bg }} onLayout={measureViewport}>
    <PageScroll
      style={[styles.container, { backgroundColor: tc.bg }]}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={tc.rosa} colors={[tc.rosa]} />}
    >
      <View style={styles.sectionLabel}>
        <View style={styles.sectionBar} />
        <Text style={styles.sectionText}>{t('pick.title')}</Text>
      </View>

      {error && <ErrorState message={error} onRetry={() => { setError(null); setLoading(true); loadData() }} />}

      {allData.length === 0 ? (
        <EmptyState icon="📅" title={t('pick.no_races_title')} subtitle={t('pick.no_races_sub')} />
      ) : (
        <>
          <RaceSwitcher
            pills={racePills}
            nextMore={racesNextMore}
            past={racesPastMore}
            selected={selGara?.id}
            onSelect={onGaraSelect}
          />
          <View style={styles.divider} />
          {stageItems.length > 0 && (
            <StageSwitcher stages={stageItems} selected={selTappa?.id_stage} onSelect={onTappaSelect} />
          )}
          <View style={styles.dividerBottom} />
          {selTappa && <StageProfile idStage={selTappa.id_stage} />}

          {isAdmin && selGara && selTappa && (
            <View style={styles.adminLinks}>
              <TouchableOpacity accessibilityRole="link" style={styles.adminBtn} onPress={() => Linking.openURL(`https://admin.virtuacycling.com/startlist?id_race=${selGara.id}&id_stage=${selTappa.id_stage}`)}>
                <MaterialCommunityIcons name="clipboard-list-outline" size={13} color={colors.flip} />
                <Text style={styles.adminBtnText}> Carica Startlist</Text>
              </TouchableOpacity>
              <TouchableOpacity accessibilityRole="link" style={styles.adminBtn} onPress={() => Linking.openURL(`https://admin.virtuacycling.com/risultati?id_race=${selGara.id}&id_stage=${selTappa.id_stage}`)}>
                <MaterialCommunityIcons name="trophy-outline" size={13} color={colors.flip} />
                <Text style={styles.adminBtnText}> Carica Risultati</Text>
              </TouchableOpacity>
            </View>
          )}

          {garaBloccata && (
            <View style={styles.blockedCard}>
              <Text style={styles.blockedTitle}>{t('pick.banned_title')}</Text>
              <Text style={styles.blockedCount}>{t('pick.banned_stages', { count: garaBloccata.stages_missed })}</Text>
              <Text style={styles.blockedDetail}>{t('pick.banned_detail')} <Text style={{ color: tc.rosaText, fontWeight: '700' }}>{t('pick.banned_message')}</Text></Text>
            </View>
          )}

          {selTappa && selGara && !garaBloccata && (
            showAllPicks ? (
              <View style={{ paddingHorizontal: 8, paddingBottom: 8 }}>
                <Alert type="info">
                  {selTappa.stage_status === 'finished' ? t('pick.stage_finished') : t('pick.live_alert')}
                </Alert>

                <TouchableOpacity style={styles.startlistBtn} onPress={() => setShowStartlist(true)} accessibilityRole="button">
                  <MaterialCommunityIcons name="clipboard-list-outline" size={16} color={tc.rosaText} />
                  <Text style={styles.startlistBtnText}>{t('pick.startlist_btn')}</Text>
                </TouchableOpacity>
                <ModaleStartlist visible={showStartlist} idRace={selGara.id} raceName={selGara.name} onClose={() => setShowStartlist(false)} />

                <View style={styles.liveCard}>
                  <View style={styles.liveHeader}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <Image
                        source={LOGO}
                        style={styles.liveHeaderLogo}
                        resizeMode="contain"
                      />
                      <View>
                        <Text style={styles.liveHeaderRace}>{selGara.name}</Text>
                        <Text style={styles.liveHeaderStage}>
                          {selGara.type === 3 ? t('common.one_day_race') : `${t('common.stage')} ${selTappa.stage}`}
                        </Text>
                      </View>
                    </View>
                    <View style={{ alignItems: 'flex-end', gap: 6 }}>
                      {showShareOptions && (
                        <View style={{ flexDirection: 'row', gap: 4 }}>
                          {[{ key: 'top10', label: 'Top 10' }, { key: 'top25', label: 'Top 25' }, { key: 'all', label: t('pick.share_all') },
                          ].map(opt => (
                            <TouchableOpacity key={opt.key} onPress={() => setShareMode(opt.key)} style={[styles.shareModeChip, shareMode === opt.key && styles.shareModeChipActive]}>
                              <Text style={[styles.shareModeChipText, shareMode === opt.key && styles.shareModeChipTextActive]}>{opt.label}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                      <View style={{ flexDirection: 'row', gap: 6 }}>
                        {showShareOptions && (
                          <TouchableOpacity style={styles.shareBtnCancel} onPress={() => setShowShareOptions(false)}>
                            <Ionicons name="close" size={14} color={tc.textMuted} />
                          </TouchableOpacity>
                        )}
                        <TouchableOpacity style={[styles.shareBtn, sharing && styles.shareBtnDisabled]} onPress={handleShare} disabled={sharing}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                            {sharing
                              ? <ActivityIndicator size={12} color={tc.brandText} />
                              : <MaterialCommunityIcons name="share-variant-outline" size={13} color={tc.brandText} />
                            }
                            <Text style={styles.shareBtnText}>
                              {sharing ? '...' : showShareOptions ? t('common.send') : t('leaderboard.share')}
                            </Text>
                          </View>
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>

                  <JerseyAsOf date={selTappa?.stage_date} stageId={selTappa?.id_stage}>
                  <ErrorBoundary label="TabellaLive">
                    <TabellaLive
                      viewShotRef={viewShotRef}
                      idStage={selTappa.id_stage}
                      idRace={selGara.id}
                      prevStageId={tappeGara.filter(tappa => tappa.stage < selTappa.stage).sort((a, b) => b.stage - a.stage)[0]?.id_stage ?? null}
                      isTour={selGara.type !== 3}
                      nickname={nickname}
                      leagueId={leagueId}
                      colors={selGara.colors}
                      onDataReady={handleDataReady}
                      shareHeader={{ date: selTappa?.stage_date, raceName: selGara.name, stage: selTappa.stage, isODR: selGara.type === 3, leagueName: leagueName, theme: profile?.theme ?? 'dark', year: selTappa?.stage_date ? new Date(selTappa.stage_date).getFullYear() : new Date().getFullYear() }}
                      shareMode={shareMode}
                      finished={selTappa.stage_status === 'finished'}
                    />
                  </ErrorBoundary>
                  </JerseyAsOf>

                  {leagueId === 4 && (
                    <ErrorBoundary label="TabellaLivePietre">
                      <TabellaLivePietre idStage={selTappa.id_stage} />
                    </ErrorBoundary>
                  )}
                </View>
              </View>
            ) : (
              <>
                {isTour && (
                  <SegmentedTabs
                    options={[{ key: 'inserimento', label: t('pick.tab_insert') }, { key: 'riepilogo', label: t('pick.tab_summary') }]}
                    value={activeTab}
                    onChange={setActiveTab}
                    style={{ marginHorizontal: 16, marginBottom: 16 }}
                  />
                )}
                <View style={[styles.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]}>
                  {activeTab === 'inserimento' ? (
                    selTappa?.stage_status === 'upcoming' && !selTappa?.has_start_list ? (
                      <View style={{ padding: 20 }}>
                        <EmptyState icon="⏳" title={t('pick.start_list_title')} subtitle={t('pick.start_list_sub')} />
                      </View>
                    ) : (
                      <InserimentoPick
                        key={`${selGara.id}-${selTappa.id_stage}-${leagueId}`}
                        userId={user?.id} selGara={selGara} selTappa={selTappa}
                        leagueId={leagueId} hasFlip={hasFlip}
                      />
                    )
                  ) : (
                    <View style={{ padding: 16 }}>
                      <RiepilogoPick userId={user?.id} raceId={selGara.id} leagueId={leagueId} />
                    </View>
                  )}
                </View>
              </>
            )
          )}
        </>
      )}
    </PageScroll>
    {stickyNode && <View style={styles.sticky} pointerEvents="box-none">{stickyNode}</View>}
    </View>
    </PickScrollContext.Provider>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { flex: 1 },
  sticky: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 30, elevation: 8 },
  content: { paddingBottom: 32 },
  loadingWrap: { flex: 1, padding: 20, backgroundColor: tc.bg },
  loadingText: { color: tc.textMuted, fontSize: 14 },
  sectionLabel: { flexDirection: 'row', alignItems: 'center', padding: 16, paddingBottom: 0, gap: 10 },
  sectionBar: { width: 3, height: 18, backgroundColor: tc.rosa, borderRadius: 2 },
  sectionText: { fontSize: 17, fontFamily: fonts.condensedBold, color: tc.text, textTransform: 'uppercase', letterSpacing: 0.5 },
  divider: { height: 1, backgroundColor: tc.border, marginHorizontal: 16 },
  dividerBottom: { height: 1, backgroundColor: tc.border, marginHorizontal: 16, marginBottom: 16 },
  startlistBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 8, paddingVertical: 10, borderRadius: radius.md, borderWidth: 1, borderColor: tc.border, backgroundColor: tc.card },
  startlistBtnText: { fontSize: 14, fontFamily: fonts.condensedBold, color: tc.rosaText, letterSpacing: 0.5, textTransform: 'uppercase' },
  adminLinks: { flexDirection: 'row', gap: 8, paddingHorizontal: 16, marginBottom: 12 },
  adminBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.flipBg, borderWidth: 1, borderColor: colors.flipBorder, borderRadius: radius.md, padding: 8 },
  adminBtnText: { fontSize: 12, fontFamily: fonts.condensedBold, color: colors.flip, letterSpacing: 0.5, textTransform: 'uppercase' },
  blockedCard: { marginHorizontal: 8, marginBottom: 12, backgroundColor: tc.rosaSubtle, borderWidth: 1, borderLeftWidth: 4, borderColor: tc.rosaBorder, borderRadius: radius.lg, padding: 16 },
  blockedTitle: { fontFamily: fonts.condensedBold, fontSize: 15, color: tc.rosaText, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  blockedCount: { fontSize: 13, color: tc.text, lineHeight: 20, marginBottom: 4 },
  blockedDetail: { fontSize: 12, color: tc.textMuted },
  scoutCard: { marginBottom: 12, backgroundColor: tc.bgCard, borderWidth: 1, borderColor: tc.border, borderLeftWidth: 3, borderLeftColor: tc.rosa, borderRadius: radius.lg, overflow: 'hidden' },
  liveCard: { backgroundColor: tc.bgCard, borderRadius: radius.lg, overflow: 'hidden', borderWidth: 1, borderColor: tc.border },
  liveHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: tc.border },
  liveHeaderLogo: { width: 28, height: 28, borderRadius: 6 },
  liveHeaderRace: { fontFamily: fonts.condensedBold, fontSize: 13, color: tc.text, textTransform: 'uppercase', lineHeight: 16 },
  liveHeaderStage: { fontSize: 11, color: tc.textMuted },
  shareBtn: { backgroundColor: tc.rosa, borderRadius: radius.md, paddingHorizontal: 14, paddingVertical: 7 },
  shareModeChip: { borderRadius: 6, borderWidth: 1, borderColor: tc.border, paddingHorizontal: 8, paddingVertical: 3, backgroundColor: tc.bgCard2 },
  shareModeChipActive: { borderColor: tc.rosa, backgroundColor: tc.rosaSubtle },
  shareModeChipText: { fontSize: 10, color: tc.textMuted, fontFamily: fonts.condensedBold },
  shareModeChipTextActive: { color: tc.rosaText },
  shareBtnCancel: { borderRadius: radius.md, paddingHorizontal: 10, paddingVertical: 7, borderWidth: 1, borderColor: tc.border, backgroundColor: tc.bgCard2, alignItems: 'center', justifyContent: 'center' },
  shareBtnCancelText: { color: tc.textMuted, fontSize: 12 },
  shareBtnDisabled: { backgroundColor: tc.bgCard2 },
  shareBtnText: { color: tc.brandText, fontSize: 12, fontFamily: fonts.condensedBold },
  card: { marginHorizontal: 16, borderRadius: radius.lg, borderWidth: 1, overflow: 'hidden' },
})